import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, Fragment } from 'react';
import _ from 'lodash';
import useResizeObserver from '../chart/useResizeObserver';
import { corrChartColors } from '../enhancedFeatures/clusterColors';
import ClusterChartView from './ClusterChartView';

const ClusterVisualView = props => {
  const { chartView, contourMap, selectedCluster, clusterResponse, clusterSegmentCount } = props.project;
  const {
    thresholds,
    scatOp,
    cellSize,
    bandWidth,
    interval,
    clustersVisible,
  } = props;

  const [viewLabel, setViewLabel] = useState('Front');
  const [chartStyle, setChartStyle] = useState('both');

  const [cData, setCData] = useState([]);
 
  const [dimensions, setDimensions] = useState({ width: 1000, height: 500 });

  // hardwired options
  // const bandWidth = 20;
  // const thresholds = 30;
  // const scatOp = 8;
  // const cellSize = 4;

  const wrapperRef = useRef();
  const resizeObserver = useResizeObserver(wrapperRef);

  const ro = resizeObserver && resizeObserver.width;
  useEffect(() => {
    const dims = { ...dimensions };
    if (resizeObserver) {
      dims.width = resizeObserver.width;
      dims.height = resizeObserver.height;
    } else if (wrapperRef.current) {
      dims.width = wrapperRef.current.clientWidth;
      dims.height = wrapperRef.current.clientHeight;
    }
    setDimensions(dims);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ro, wrapperRef]);

  useEffect(() => {
    setCData([]);
  }, [chartView]);

  const gData =
    contourMap && contourMap.data
      ? _.toPairs(_.groupBy(contourMap.data, x => x.cid))
      : [];

  useEffect(() => {
    if (gData.length === 0) return;

    const getX = d => (chartView[0] === 1 ? d.x : chartView[0] === 2 ? d.y : d.z);
    const getY = d => (chartView[1] === 1 ? d.x : chartView[1] === 2 ? d.y : d.z);

    const calcAvgDistanceFromCenter = (cX, cY, dd) => {
      const s = _.sumBy(dd, d => (cX - getX(d)) ** 2 + (cY - getY(d)) ** 2);
      const avgDist = Math.sqrt(s);
      return dd.length / avgDist;
    };

    try {
      const cluster =
        clusterResponse.solutions[selectedCluster];
      const data = [];

      cluster.segments.forEach((segment, i) => {
        const bb = gData[i];
        const centX = _.sumBy(bb[1], d => getX(d)) / bb[1].length;
        const centY = _.sumBy(bb[1], d => getY(d)) / bb[1].length;
        data.push({
          name: segment.segmentName,
          segment,
          data: bb[1],
          color: corrChartColors[i].color,
          visible: clustersVisible[i],
          centerX: centX,
          centerY: centY,
          avgDist: calcAvgDistanceFromCenter(centX, centY, bb[1]),
        });
      });

      setCData(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(
        `%cERROR: %${e}`,
        'color: blue; font-size:12px;',
        'color: red; font-size:16px;'
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gData.length, clusterSegmentCount, clustersVisible]);

  return (
    <Fragment>
      <div
        className="outerDiv"
        ref={wrapperRef}
        id="correspondenceClusterChart"
      >
        <div className="corrD3_wrapperDiv">
          <div className="corrD3_innerDiv">
            {contourMap && contourMap.data && (
              <ClusterChartView
                ContourData={contourMap.data}
                cData={cData}
                chartView={chartView}
                chartStyle={chartStyle}
                bandWidth={bandWidth}
                thresholds={thresholds}
                interval={interval}
                dimensions={dimensions}
                scatterOp={scatOp}
                cellSize={cellSize}
              />
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

ClusterVisualView.propTypes = {
  project: PropTypes.shape({
    contourMap: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    clusterResponse: PropTypes.shape({
      solutions: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    selectedCluster: PropTypes.number,
    clusterSegmentCount: PropTypes.number,
    chartView: PropTypes.arrayOf(PropTypes.number),
  }),
  bandWidth: PropTypes.number,
  thresholds: PropTypes.number,
  scatOp: PropTypes.number,
  cellSize: PropTypes.number,
  interval: PropTypes.number,
  clustersVisible: PropTypes.arrayOf(PropTypes.bool),
};

export default ClusterVisualView;
